/**
 * How an activity CATEGORY is written down — the single definition, shared by the browser
 * and the API so the two can never disagree.
 *
 * WHY THIS EXISTS. `Activity.category` was free text. Postgres compares strings exactly, and
 * the list endpoint filters with `where.category = input.category`, so "travel", "Travel" and
 * "TRAVEL" were three separate categories: filtering by one silently missed the others, and a
 * year-end total would show the same spending split across three lines. Nothing errored —
 * the number was simply wrong.
 *
 * The fix is one canonical spelling per category. Every value is stored Title Cased, and two
 * categories are "the same" when they match ignoring case and surrounding space. The picker
 * therefore offers an existing category rather than creating a near-duplicate of it.
 *
 * Internal capitals are deliberately NOT preserved: "VAT" becomes "Vat". Keeping them would
 * mean "VAT" and "Vat" are two spellings again, which is the whole problem. One rule, applied
 * to everything, beats a special case that reintroduces duplicates.
 */

/** Title Case, whitespace collapsed: "  court   FEES " → "Court Fees". */
export function normaliseCategory(raw: string): string {
  return raw
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase()
    .replace(/(^|[\s\-/('])([a-z])/g, (_m, lead: string, ch: string) => lead + ch.toUpperCase());
}

/** True when two categories are the same category, however they were typed. */
export function categoriesMatch(a: string, b: string): boolean {
  return normaliseCategory(a).toLowerCase() === normaliseCategory(b).toLowerCase();
}

/** Longest a category may be — mirrors the `category` column and the API schema. */
export const CATEGORY_MAX_LENGTH = 100;
